import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Send, MessageCircle, Trash2 } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import { useTranslation } from 'react-i18next';
import QuantitySelector from '@/components/ui/QuantitySelector';
import Button from '@/components/ui/Button';

export default function CartDrawer() {
  const { t } = useTranslation();
  const { items, isOpen, setCartOpen, updateQuantity, removeItem } = useCartStore();
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const close = () => setCartOpen(false);

  const buildCartText = () => {
    return encodeURIComponent(
      `${t('buy.cart.whatsappText')}\n\n${items.map((i) => `- ${i.name}: ${i.quantity}`).join('\n')}\n\nNombre: ${name || ''}\nTeléfono: ${phone || ''}`
    );
  };

  const whatsappLink = `https://wa.me/50687765128?text=${buildCartText()}`;

  const emailBody = encodeURIComponent(
    `${t('buy.cart.whatsappText')}\n\n${items.map((i) => `- ${i.name}: ${i.quantity}`).join('\n')}\n\nNombre: ${name || ''}\nTeléfono: ${phone || ''}`
  );
  const emailLink = `mailto:info@cafelossauces.com?subject=${encodeURIComponent(t('buy.cart.emailSubject'))}&body=${emailBody}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />

          {/* Drawer */}
          <motion.div
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[70] flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-coffee-100">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-6 h-6 text-coffee-400" />
                <h2 className="font-display text-xl text-coffee-900">
                  {t('buy.cart.title')} ({totalItems})
                </h2>
              </div>
              <button
                onClick={close}
                className="p-2 rounded-full hover:bg-coffee-100 transition-colors"
                aria-label="Cerrar carrito"
              >
                <X className="w-5 h-5 text-coffee-900" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-coffee-500">
                  <ShoppingCart className="w-12 h-12 mb-3 opacity-40" />
                  <p className="text-sm">{t('buy.cart.empty')}</p>
                </div>
              ) : (
                items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 bg-coffee-50 rounded-xl p-3"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-coffee-900 truncate">
                        {item.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <QuantitySelector
                        value={item.quantity}
                        onChange={(q) => updateQuantity(item.id, q)}
                        min={1}
                      />
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-2 rounded-full hover:bg-terracotta-400/20 text-coffee-500 hover:text-terracotta-500 transition-colors"
                        aria-label={t('buy.cart.remove')}
                        title={t('buy.cart.remove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-6 py-5 border-t border-coffee-100 space-y-4">
                <div className="space-y-3">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('buy.cart.namePlaceholder')}
                    className="w-full bg-coffee-50 border border-coffee-200 rounded-xl px-4 py-2.5 text-sm text-coffee-900 placeholder:text-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-400/40"
                  />
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t('buy.cart.phonePlaceholder')}
                    className="w-full bg-coffee-50 border border-coffee-200 rounded-xl px-4 py-2.5 text-sm text-coffee-900 placeholder:text-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-400/40"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="primary"
                    size="md"
                    className="w-full"
                    leftIcon={<MessageCircle className="w-4 h-4" />}
                    onClick={() => window.open(whatsappLink, '_blank')}
                  >
                    Enviar por WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    size="md"
                    className="w-full"
                    leftIcon={<Send className="w-4 h-4" />}
                    onClick={() => window.location.href = emailLink}
                  >
                    Enviar por Email
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
